// Global Instructions Rule Applied!
//
// LP Allocation grid state (LP agents UAT follow-ups PR E): the same shape
// as the Asset Transactions slice — rows, active filters / sorting, paging —
// so the canonical grid kit (TableView infinite scroll, ViewConfig, user
// preferences) drives it unchanged.
import {
  SET_LP_ALLOCATION,
  APPEND_LP_ALLOCATION,
  SET_ACTIVE_FILTERS,
  SET_ACTIVE_SORTING,
  UPDATE_LP_ALLOCATION_ROW
} from './constants.ts'
import { LpAllocationRow } from '../../data_models/relationships_model.ts'
import { FilterValue, SortConfig } from '../../../app_modules/global_configs/tableViewPreferences.ts'

export interface LpAllocationPaginatedResponse {
  data: LpAllocationRow[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface LpAllocationState {
  data: LpAllocationRow[]
  totalCount: number
  activeFilters: FilterValue[]
  activeSorting: SortConfig[]
  hasMore: boolean
  currentPage: number
}

type LpAllocationAction =
  | { type: typeof SET_LP_ALLOCATION; payload: { response: LpAllocationPaginatedResponse } }
  | { type: typeof APPEND_LP_ALLOCATION; payload: { response: LpAllocationPaginatedResponse } }
  | { type: typeof SET_ACTIVE_FILTERS; payload: { filters: FilterValue[] } }
  | { type: typeof SET_ACTIVE_SORTING; payload: { sorting: SortConfig[] } }
  | { type: typeof UPDATE_LP_ALLOCATION_ROW; payload: { accountId: string; updates: Partial<LpAllocationRow> } }

const initialState: LpAllocationState = {
  data: [],
  totalCount: 0,
  activeFilters: [],
  activeSorting: [],
  hasMore: false,
  currentPage: 1
}

const lpAllocationReducer = (state: LpAllocationState = initialState, action: LpAllocationAction): LpAllocationState => {
  switch (action.type) {
    case SET_LP_ALLOCATION: {
      const { response } = action.payload
      return { ...state, data: response.data, totalCount: response.totalCount, hasMore: response.hasMore, currentPage: 1 }
    }
    case APPEND_LP_ALLOCATION: {
      const { response } = action.payload
      return {
        ...state,
        data: [...state.data, ...response.data],
        totalCount: response.totalCount,
        hasMore: response.hasMore,
        currentPage: state.currentPage + 1
      }
    }
    case SET_ACTIVE_FILTERS:
      return { ...state, activeFilters: action.payload.filters }
    case SET_ACTIVE_SORTING:
      return { ...state, activeSorting: action.payload.sorting }
    case UPDATE_LP_ALLOCATION_ROW: {
      const { accountId, updates } = action.payload
      if (!state.data.some((r) => r.accounts__id === accountId)) return state
      return { ...state, data: state.data.map((r) => (r.accounts__id === accountId ? { ...r, ...updates } : r)) }
    }
    default:
      return state
  }
}

export default lpAllocationReducer

export const setLpAllocation = (payload: { response: LpAllocationPaginatedResponse }) => ({ type: SET_LP_ALLOCATION, payload })
export const appendLpAllocation = (payload: { response: LpAllocationPaginatedResponse }) => ({ type: APPEND_LP_ALLOCATION, payload })
export const setLpAllocationActiveFilters = (payload: { filters: FilterValue[] }) => ({ type: SET_ACTIVE_FILTERS, payload })
export const setLpAllocationActiveSorting = (payload: { sorting: SortConfig[] }) => ({ type: SET_ACTIVE_SORTING, payload })
export const updateLpAllocationRow = (payload: { accountId: string; updates: Partial<LpAllocationRow> }) => ({
  type: UPDATE_LP_ALLOCATION_ROW,
  payload
})
